import { useState, useEffect, useMemo } from 'react'
import { 
  ScatterChart,
  Scatter,
  BarChart,
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  Cell,
  ZAxis
} from 'recharts'
import { AlertCircle, Package, Palette, Info, Database, ChevronDown, ChevronUp, Loader2, Smartphone, Eye, Box, HelpCircle, Clock, TrendingUp, TrendingDown } from 'lucide-react'
import { getOperatorColors, formatNumber, formatPercent } from '../utils/chartHelpers'

/**
 * Data source selector component
 */
const DataSourceSelector = ({ value, onChange }) => {
  return (
    <div className="bg-gray-50 rounded-lg p-4">
      <div className="flex items-center mb-3">
        <Database className="h-4 w-4 mr-2 text-gray-600" />
        <span className="text-sm font-medium text-gray-700">Źródło danych</span>
      </div>
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2.5 pr-10 text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
        >
          <option value="operator">Dane od operatora</option>
          <option value="kam">Dane zebrane przez KAM</option>
        </select>
        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
      </div>
      <p className="text-xs text-gray-500 mt-2">
        {value === 'operator' 
          ? 'Dane pochodzą bezpośrednio od operatora (jeśli udostępnia)' 
          : 'Dane zebrane podczas wizyt przez zespół KAM'}
      </p>
    </div>
  )
}

/**
 * Salon scope toggle component
 */
const SalonScopeToggle = ({ value, onChange }) => {
  return (
    <div className="bg-gray-50 rounded-lg p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <Box className="h-4 w-4 mr-2 text-gray-600" />
          <span className="text-sm font-medium text-gray-700">Uwzględnij salony bez sprzedaży</span>
        </div>
        <button
          onClick={() => onChange(!value)}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
            value ? 'bg-blue-500' : 'bg-gray-300'
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              value ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>
      <p className="text-xs text-gray-500 mt-2">
        {value 
          ? 'Analiza obejmuje wszystkie salony operatora' 
          : 'Analiza obejmuje tylko salony ze sprzedażą > 0'}
      </p>
    </div>
  )
}

/**
 * Data Sources Info Section - expandable panel explaining where data comes from
 */
const DataSourcesInfo = () => {
  const [isExpanded, setIsExpanded] = useState(false)

  const dataSources = [
    {
      title: 'Dane o sprzedaży',
      icon: Package,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      description: 'Dane sprzedażowe pochodzą z systemów operatorów (Orange, Play, Plus, T-Mobile) i są agregowane dla każdego salonu.',
      source: 'Bazy danych operatorów'
    },
    {
      title: 'Zatowarowanie (stock)',
      icon: Box,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      description: 'Poziom zatowarowania (ilość produktów na stanie) pochodzi z danych stockowych operatora. Kategoryzowany jako: wysoki (>10), średni (3-10), niski (1-2), brak (0).',
      source: 'System stockowy operatora'
    },
    {
      title: 'Ekspozycja (Operator)',
      icon: Palette,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      description: 'Ekspozycja wg danych operatora - atrybut salonu ID 29 (wizualizacja wg operatora). Jeśli brak danych operatora, używane są dane KAM jako fallback.',
      source: 'Tabela: kam_sfa.salon_attribute_value (attr 29)'
    },
    {
      title: 'Atrybut: Zatowarowanie',
      icon: Box,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
      description: 'Atrybut salonu (ID: 31) określający czy salon posiada zatowarowanie produktami operatora. Wartość boolean ustawiana przez zespół KAM.',
      source: 'Tabela: kam_sfa.salon_attribute_value (attribute_id = 31)'
    },
    {
      title: 'Ekspozycja (KAM)',
      icon: Eye,
      color: 'text-amber-600',
      bgColor: 'bg-amber-50',
      description: 'Ekspozycja wg KAM jest prawdziwa jeśli: atrybut salonu (ID: 33) = true LUB ostatnia wizyta ma current_visualization_in_place = true. Spójne z danymi na Overview.',
      source: 'Tabela: kam_sfa.salon_attribute_value (attr 33) OR kam_sfa.visit'
    },
    {
      title: 'Atrybut: Urządzenie ambasadorskie',
      icon: Smartphone,
      color: 'text-rose-600',
      bgColor: 'bg-rose-50',
      description: 'Atrybut salonu (ID: 28) określający czy do salonu przekazano urządzenie demonstracyjne. Wartość boolean ustawiana przez zespół KAM.',
      source: 'Tabela: kam_sfa.salon_attribute_value (attribute_id = 28)'
    }
  ]

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-6">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors rounded-xl"
      >
        <div className="flex items-center">
          <HelpCircle className="h-5 w-5 text-blue-500 mr-3" />
          <span className="font-medium text-gray-800">Źródła danych w raporcie</span>
        </div>
        {isExpanded ? (
          <ChevronUp className="h-5 w-5 text-gray-400" />
        ) : (
          <ChevronDown className="h-5 w-5 text-gray-400" />
        )}
      </button>

      {isExpanded && (
        <div className="px-4 pb-4 border-t border-gray-100">
          <p className="text-sm text-gray-500 mt-3 mb-4">
            Poniżej znajduje się opis źródeł danych wykorzystywanych w tym raporcie. 
            Dane pochodzą z różnych systemów i są łączone w celu analizy wpływu poszczególnych czynników na sprzedaż.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {dataSources.map((source, index) => {
              const Icon = source.icon
              return (
                <div 
                  key={index} 
                  className={`${source.bgColor} rounded-lg p-3 border border-opacity-20`}
                >
                  <div className="flex items-start">
                    <Icon className={`h-5 w-5 ${source.color} mr-2 mt-0.5 flex-shrink-0`} />
                    <div>
                      <h4 className={`text-sm font-semibold ${source.color.replace('-600', '-800')}`}>
                        {source.title}
                      </h4>
                      <p className="text-xs text-gray-600 mt-1">
                        {source.description}
                      </p>
                      <p className="text-xs text-gray-400 mt-1 font-mono">
                        {source.source}
                      </p>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>

          <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
            <p className="text-xs text-blue-700">
              <strong>Uwaga:</strong> Dane o zatowarowaniu i ekspozycji pochodzą z wizyt KAM i systemu stockowego. 
              Dane o urządzeniu ambasadorskim pochodzą z atrybutów salonów wprowadzanych przez zespół KAM.
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * Metric Card for stock/exposition
 */
const MetricCard = ({ title, value, subtitle, variant = 'default' }) => {
  const variants = {
    default: 'bg-white border-brand-gray-200',
    success: 'bg-green-50 border-green-200',
    info: 'bg-blue-50 border-blue-200',
    warning: 'bg-amber-50 border-amber-200'
  }

  const valueColors = {
    default: 'text-brand-gray-900',
    success: 'text-green-700',
    info: 'text-blue-700',
    warning: 'text-amber-700'
  }

  return (
    <div className={`rounded-xl border shadow-sm p-4 ${variants[variant]}`}>
      <p className="text-sm font-medium text-brand-gray-600 mb-1">{title}</p>
      <p className={`text-2xl font-bold ${valueColors[variant]}`}>{value}</p>
      {subtitle && (
        <p className="text-xs text-brand-gray-500 mt-1">{subtitle}</p>
      )}
    </div>
  )
}

/**
 * Custom tooltip for scatter chart
 */
const ScatterTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null
  
  const data = payload[0].payload
  
  return (
    <div className="bg-white border border-brand-gray-200 rounded-lg shadow-lg p-3">
      <p className="font-semibold text-brand-gray-800 mb-1">{data.salon_name}</p>
      <p className="text-sm text-brand-gray-600">
        Zatowarowanie: <span className="font-medium">{data.estimated_stock_today} szt.</span>
      </p>
      <p className="text-sm text-brand-gray-600">
        Sprzedaż: <span className="font-medium">{data.total_sales} szt.</span>
      </p>
      <p className="text-sm text-brand-gray-500 mt-1 italic">
        Kategoria: {data.stock_category}
      </p>
    </div>
  )
}

/**
 * Custom tooltip for bar chart
 */
const BarTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null
  
  const data = payload[0].payload
  
  return (
    <div className="bg-white border border-brand-gray-200 rounded-lg shadow-lg p-3">
      <p className="font-semibold text-brand-gray-800 mb-1">{data.category?.replaceAll('\n', ' ') || data.name}</p>
      <p className="text-sm text-brand-gray-600">
        Średnia: <span className="font-medium">{data.avg_sales?.toFixed(2)} szt.</span>
      </p>
      <p className="text-sm text-brand-gray-600">
        Liczba salonów: <span className="font-medium">{data.count}</span>
      </p>
      <p className="text-sm text-brand-gray-600">
        Łączna sprzedaż: <span className="font-medium">{data.total} szt.</span>
      </p>
    </div>
  )
}

/**
 * Reusable Attribute Impact Section Component
 */
const AttributeImpactSection = ({ 
  title, 
  icon: Icon, 
  data, 
  withLabel, 
  withoutLabel,
  colors,
  description 
}) => {
  if (!data) return null

  const diffPct = data.avg_sales_diff_pct || 0
  const chartData = [
    {
      name: withLabel,
      avg_sales: data.with_attribute?.avg_sales || 0,
      count: data.with_attribute?.count || 0,
      total: data.with_attribute?.total_sales || 0
    },
    {
      name: withoutLabel,
      avg_sales: data.without_attribute?.avg_sales || 0,
      count: data.without_attribute?.count || 0,
      total: data.without_attribute?.total_sales || 0
    }
  ]

  return (
    <div className="mt-8">
      <div className="flex items-center mb-4">
        <Icon className="h-5 w-5 mr-2 text-gray-600" />
        <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
      </div>

      {description && (
        <p className="text-sm text-gray-500 mb-4">{description}</p>
      )}

      {/* Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <MetricCard
          title={withLabel}
          value={data.with_attribute?.count || 0}
          subtitle={`Śr. sprzedaż: ${(data.with_attribute?.avg_sales || 0).toFixed(1)} szt.`}
          variant="success"
        />
        <MetricCard
          title={withoutLabel}
          value={data.without_attribute?.count || 0}
          subtitle={`Śr. sprzedaż: ${(data.without_attribute?.avg_sales || 0).toFixed(1)} szt.`}
          variant="info"
        />
        <MetricCard
          title="Różnica w średniej"
          value={`${diffPct > 0 ? '+' : ''}${diffPct.toFixed(1)}%`}
          subtitle={diffPct > 0 ? 'Pozytywny wpływ' : diffPct < 0 ? 'Negatywny wpływ' : 'Brak wpływu'}
          variant={diffPct > 0 ? 'success' : diffPct < 0 ? 'warning' : 'default'}
        />
      </div>

      {/* Comparison Bar Chart */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
        <h4 className="text-sm font-semibold text-gray-700 mb-4">
          Porównanie średniej sprzedaży
        </h4>
        
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              margin={{ top: 10, right: 30, left: 40, bottom: 30 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis 
                dataKey="name" 
                tick={{ fontSize: 12, fill: '#6b7280' }}
              />
              <YAxis 
                tick={{ fontSize: 11, fill: '#6b7280' }}
                label={{ 
                  value: 'Średnia sprzedaż (szt.)', 
                  angle: -90, 
                  position: 'insideLeft',
                  style: { fontSize: 11, fill: '#6b7280' }
                }}
              />
              <Tooltip content={<BarTooltip />} />
              <Bar dataKey="avg_sales" radius={[4, 4, 0, 0]}>
                <Cell fill={colors.primary} />
                <Cell fill="#9ca3af" />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Interpretation */}
        <div className={`mt-4 p-4 rounded-lg ${
          diffPct > 0 ? 'bg-green-50 border border-green-200' :
          diffPct < 0 ? 'bg-amber-50 border border-amber-200' :
          'bg-gray-50 border border-gray-200'
        }`}>
          {diffPct > 0 ? (
            <p className="text-sm text-green-700">
              <span className="font-bold">Pozytywny wpływ:</span> Salony z tym atrybutem sprzedają średnio o {diffPct.toFixed(1)}% więcej.
            </p>
          ) : diffPct < 0 ? (
            <p className="text-sm text-amber-700">
              <span className="font-bold">Negatywna korelacja:</span> Salony bez tego atrybutu sprzedają średnio o {Math.abs(diffPct).toFixed(1)}% więcej.
            </p>
          ) : (
            <p className="text-sm text-gray-600">
              <span className="font-bold">Brak wpływu:</span> Atrybut nie ma zauważalnego wpływu na średnią sprzedaż.
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * Stock Exposition sub-report component
 */
const StockExposition = ({ 
  data,
  operator,
  loading,
  onFetchData
}) => {
  const [dataSource, setDataSource] = useState('kam')
  const [includeAllSalons, setIncludeAllSalons] = useState(false)
  const colors = getOperatorColors(operator)

  // Fetch data when component mounts or settings change
  useEffect(() => {
    if (onFetchData) {
      onFetchData(dataSource, includeAllSalons)
    }
  }, [dataSource, includeAllSalons, onFetchData])

  // Stock category colors
  const stockColors = {
    high: colors.primary,
    medium: colors.secondary,
    low: colors.light,
    none: '#9ca3af'
  }

  // Prepare stock correlation data for bar chart
  const stockCorrelationData = useMemo(() => {
    // For KAM data, use zatowarowanie_impact (warehouse stock data, same as Overview)
    if (dataSource === 'kam') {
      if (!data?.zatowarowanie_impact) return []
      return [
        { 
          category: 'Z zatowarowaniem', 
          avg_sales: data.zatowarowanie_impact.with_attribute?.avg_sales || 0,
          count: data.zatowarowanie_impact.with_attribute?.count || 0,
          total: data.zatowarowanie_impact.with_attribute?.total_sales || 0,
          color: stockColors.high
        },
        { 
          category: 'Bez zatowarowania', 
          avg_sales: data.zatowarowanie_impact.without_attribute?.avg_sales || 0,
          count: data.zatowarowanie_impact.without_attribute?.count || 0,
          total: data.zatowarowanie_impact.without_attribute?.total_sales || 0,
          color: stockColors.none
        }
      ]
    }
    
    // For operator data, use stock_correlation with all stock level categories
    if (!data?.stock_correlation) return []
    return [
      { 
        category: 'Wysoki\nstock', 
        avg_sales: data.stock_correlation.high_stock?.avg_sales || 0,
        count: data.stock_correlation.high_stock?.count || 0,
        total: data.stock_correlation.high_stock?.total_sales || 0,
        color: stockColors.high
      },
      { 
        category: 'Średni\nstock', 
        avg_sales: data.stock_correlation.medium_stock?.avg_sales || 0,
        count: data.stock_correlation.medium_stock?.count || 0,
        total: data.stock_correlation.medium_stock?.total_sales || 0,
        color: stockColors.medium
      },
      { 
        category: 'Niski\nstock', 
        avg_sales: data.stock_correlation.low_stock?.avg_sales || 0,
        count: data.stock_correlation.low_stock?.count || 0,
        total: data.stock_correlation.low_stock?.total_sales || 0,
        color: stockColors.low
      },
      { 
        category: 'Brak\nstocku', 
        avg_sales: data.stock_correlation.none_stock?.avg_sales || 0,
        count: data.stock_correlation.none_stock?.count || 0,
        total: data.stock_correlation.none_stock?.total_sales || 0,
        color: stockColors.none
      }
    ]
  }, [data?.stock_correlation, data?.zatowarowanie_impact, dataSource])

  // Exposition comparison data
  const expositionData = useMemo(() => {
    if (!data?.exposition_impact) return null
    return [
      {
        name: 'Z ekspozycją',
        avg_sales: data.exposition_impact.with_exposition?.avg_sales || 0,
        count: data.exposition_impact.with_exposition?.count || 0,
        total: data.exposition_impact.with_exposition?.total_sales || 0
      },
      {
        name: 'Bez ekspozycji',
        avg_sales: data.exposition_impact.without_exposition?.avg_sales || 0,
        count: data.exposition_impact.without_exposition?.count || 0,
        total: data.exposition_impact.without_exposition?.total_sales || 0
      }
    ]
  }, [data?.exposition_impact])

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-brand-gray-200 shadow-sm p-8">
        <div className="flex flex-col items-center justify-center">
          <Loader2 className="h-10 w-10 text-brand-blue animate-spin mb-3" />
          <p className="text-sm text-brand-gray-500">Ładowanie danych...</p>
        </div>
      </div>
    )
  }

  if (!data) {
    return (
      <div className="bg-white rounded-xl border border-brand-gray-200 shadow-sm p-8">
        <div className="flex flex-col items-center justify-center text-center">
          <AlertCircle className="h-12 w-12 text-amber-500 mb-3" />
          <h3 className="text-lg font-semibold text-brand-gray-800 mb-1">
            Brak danych
          </h3>
          <p className="text-sm text-brand-gray-500">
            Brak danych o zatowarowaniu i ekspozycji dla tego operatora.
          </p>
        </div>
      </div>
    )
  }

  const expositionImpact = data.exposition_impact
  const diffPct = expositionImpact?.avg_sales_diff_pct || 0

  return (
    <div className="space-y-6">
      {/* Settings Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Data Source Selector */}
        <DataSourceSelector value={dataSource} onChange={setDataSource} />
        
        {/* Salon Scope Toggle */}
        <SalonScopeToggle value={includeAllSalons} onChange={setIncludeAllSalons} />
      </div>

      {/* Data Sources Info - expandable */}
      <DataSourcesInfo />

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start">
        <Info className="h-5 w-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
        <div className="text-sm text-blue-800">
          <p><span className="font-semibold">Okres analizy:</span> {data.analysis_period}</p>
          <p className="mt-1">
            <span className="font-semibold">Analizowane salony:</span> {data.total_salons} salonów
            {data.salons_with_sales !== undefined && data.salons_with_sales !== data.total_salons && (
              <span className="text-blue-600"> (w tym {data.salons_with_sales} ze sprzedażą)</span>
            )}
          </p>
          <p className="mt-1">
            <span className="font-semibold">Z zatowarowaniem:</span> {data.salons_with_zatowarowanie || 0} salonów
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
              dataSource === 'operator' 
                ? 'bg-blue-100 text-blue-800' 
                : 'bg-green-100 text-green-800'
            }`}>
              {dataSource === 'operator' ? 'Dane od operatora' : 'Dane KAM'}
            </span>
            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
              includeAllSalons 
                ? 'bg-purple-100 text-purple-800' 
                : 'bg-gray-100 text-gray-800'
            }`}>
              {includeAllSalons ? 'Wszystkie salony' : 'Tylko ze sprzedażą'}
            </span>
          </div>
        </div>
      </div>

      {/* Stock Impact Section */}
      <div>
        <div className="flex items-center mb-4">
          <Package className="h-5 w-5 mr-2 text-brand-gray-600" />
          <h3 className="text-lg font-semibold text-brand-gray-800">Wpływ zatowarowania</h3>
        </div>

        <div className={`grid gap-6 ${dataSource === 'kam' ? 'grid-cols-1' : 'grid-cols-1 lg:grid-cols-2'}`}>
          {/* Scatter Chart - Stock vs Sales (only for operator mode - scatter doesn't make sense for binary KAM data) */}
          {dataSource !== 'kam' && (
            <div className="bg-white rounded-xl border border-brand-gray-200 shadow-sm p-5">
              <h4 className="text-sm font-semibold text-brand-gray-700 mb-4">
                Korelacja: Poziom stocku vs Sprzedaż
              </h4>
              
              {data.detailed_salons?.length > 0 ? (
                <>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <ScatterChart margin={{ top: 10, right: 10, bottom: 40, left: 40 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis 
                          type="number" 
                          dataKey="estimated_stock_today" 
                          name="Stock"
                          tick={{ fontSize: 11, fill: '#6b7280' }}
                          label={{ 
                            value: 'Poziom stocku (szt.)', 
                            position: 'insideBottom', 
                            offset: -10,
                            style: { fontSize: 11, fill: '#6b7280' }
                          }}
                        />
                        <YAxis 
                          type="number" 
                          dataKey="total_sales" 
                          name="Sprzedaż"
                          tick={{ fontSize: 11, fill: '#6b7280' }}
                          label={{ 
                            value: 'Sprzedaż (szt.)', 
                            angle: -90, 
                            position: 'insideLeft',
                            style: { fontSize: 11, fill: '#6b7280' }
                          }}
                        />
                        <ZAxis range={[50, 200]} />
                        <Tooltip content={<ScatterTooltip />} />
                        <Scatter 
                          data={data.detailed_salons.filter(s => s.estimated_stock_today > 0)}
                        >
                          {data.detailed_salons.filter(s => s.estimated_stock_today > 0).map((entry, index) => (
                            <Cell 
                              key={`cell-${index}`} 
                              fill={stockColors[entry.stock_category] || stockColors.none}
                            />
                          ))}
                        </Scatter>
                      </ScatterChart>
                    </ResponsiveContainer>
                  </div>
                  
                  {/* Legend */}
                  <div className="flex flex-wrap justify-center gap-3 mt-3">
                    <div className="flex items-center">
                      <div className="w-3 h-3 rounded-full mr-1.5" style={{ backgroundColor: stockColors.high }} />
                      <span className="text-xs text-gray-600">Wysoki (&gt;10)</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-3 h-3 rounded-full mr-1.5" style={{ backgroundColor: stockColors.medium }} />
                      <span className="text-xs text-gray-600">Średni (3-10)</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-3 h-3 rounded-full mr-1.5" style={{ backgroundColor: stockColors.low }} />
                      <span className="text-xs text-gray-600">Niski (1-2)</span>
                    </div>
                  </div>
                </>
              ) : (
                <div className="h-80 flex items-center justify-center">
                  <p className="text-sm text-brand-gray-500">Brak danych o stocku</p>
                </div>
              )}
            </div>
          )}

          {/* Bar Chart - Average Sales by Stock Category */}
          <div className="bg-white rounded-xl border border-brand-gray-200 shadow-sm p-5">
            <h4 className="text-sm font-semibold text-brand-gray-700 mb-4">
              {dataSource === 'kam' 
                ? 'Średnia sprzedaż według zatowarowania (atrybut KAM)' 
                : 'Średnia sprzedaż według poziomu stocku (dane operatora)'}
            </h4>
            
            {stockCorrelationData.some(d => d.count > 0) ? (
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart 
                    data={stockCorrelationData}
                    margin={{ top: 10, right: 10, bottom: 40, left: 40 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis 
                      dataKey="category" 
                      tick={{ fontSize: 11, fill: '#6b7280' }}
                    />
                    <YAxis 
                      tick={{ fontSize: 11, fill: '#6b7280' }}
                      label={{ 
                        value: 'Średnia sprzedaż', 
                        angle: -90, 
                        position: 'insideLeft',
                        style: { fontSize: 11, fill: '#6b7280' }
                      }}
                    />
                    <Tooltip content={<BarTooltip />} />
                    <Bar dataKey="avg_sales" radius={[4, 4, 0, 0]}>
                      {stockCorrelationData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-80 flex items-center justify-center">
                <p className="text-sm text-brand-gray-500">Brak danych do wyświetlenia</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Exposition Impact Section */}
      {expositionImpact && (
        <div>
          <div className="flex items-center mb-4">
            <Palette className="h-5 w-5 mr-2 text-brand-gray-600" />
            <h3 className="text-lg font-semibold text-brand-gray-800">Wpływ ekspozycji</h3>
          </div>

          {/* Metrics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <MetricCard
              title="Z ekspozycją"
              value={expositionImpact.with_exposition?.count || 0}
              subtitle={`Śr. sprzedaż: ${(expositionImpact.with_exposition?.avg_sales || 0).toFixed(1)} szt.`}
              variant="success"
            />
            <MetricCard
              title="Bez ekspozycji"
              value={expositionImpact.without_exposition?.count || 0}
              subtitle={`Śr. sprzedaż: ${(expositionImpact.without_exposition?.avg_sales || 0).toFixed(1)} szt.`}
              variant="info"
            />
            <MetricCard
              title="Różnica w średniej"
              value={`${diffPct > 0 ? '+' : ''}${diffPct.toFixed(1)}%`}
              subtitle={diffPct > 0 ? 'Ekspozycja zwiększa sprzedaż' : 'Brak pozytywnego wpływu'}
              variant={diffPct > 0 ? 'success' : 'warning'}
            />
          </div>

          {/* Comparison Bar Chart */}
          {expositionData && (
            <div className="bg-white rounded-xl border border-brand-gray-200 shadow-sm p-5">
              <h4 className="text-sm font-semibold text-brand-gray-700 mb-4">
                Porównanie średniej sprzedaży: Z ekspozycją vs Bez ekspozycji
              </h4>
              
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={expositionData}
                    margin={{ top: 10, right: 30, left: 40, bottom: 30 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis 
                      dataKey="name" 
                      tick={{ fontSize: 12, fill: '#6b7280' }}
                    />
                    <YAxis 
                      tick={{ fontSize: 11, fill: '#6b7280' }}
                      label={{ 
                        value: 'Średnia sprzedaż (szt.)', 
                        angle: -90, 
                        position: 'insideLeft',
                        style: { fontSize: 11, fill: '#6b7280' }
                      }}
                    />
                    <Tooltip content={<BarTooltip />} />
                    <Bar dataKey="avg_sales" radius={[4, 4, 0, 0]}>
                      <Cell fill={colors.primary} />
                      <Cell fill="#9ca3af" />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Interpretation */}
              <div className={`mt-4 p-4 rounded-lg ${
                diffPct > 0 ? 'bg-green-50 border border-green-200' :
                diffPct < 0 ? 'bg-amber-50 border border-amber-200' :
                'bg-gray-50 border border-gray-200'
              }`}>
                {diffPct > 0 ? (
                  <p className="text-sm text-green-700">
                    <span className="font-bold">Pozytywny wpływ ekspozycji:</span> Salony z ekspozycją sprzedają średnio o {diffPct.toFixed(1)}% więcej niż salony bez ekspozycji.
                  </p>
                ) : diffPct < 0 ? (
                  <p className="text-sm text-amber-700">
                    <span className="font-bold">Negatywny wpływ ekspozycji:</span> Salony bez ekspozycji sprzedają średnio więcej. Może to wynikać z innych czynników wpływających na sprzedaż.
                  </p>
                ) : (
                  <p className="text-sm text-gray-600">
                    <span className="font-bold">Brak różnicy:</span> Ekspozycja nie ma zauważalnego wpływu na średnią sprzedaż.
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Ambassador Device Impact (Attribute ID 28) */}
      <AttributeImpactSection
        title="Wpływ Urządzenia Ambasadorskiego"
        icon={Smartphone}
        data={data?.ambassador_device_impact}
        withLabel="Z urządzeniem ambasadorskim"
        withoutLabel="Bez urządzenia ambasadorskiego"
        colors={colors}
        description="Urządzenie ambasadorskie to urządzenie demonstracyjne przekazane do salonu, służące do prezentacji produktów klientom."
      />

      {/* Historical Analysis Section - based on visit data */}
      {data?.historical_analysis && (
        <div className="mt-8 border-t border-gray-200 pt-8">
          <div className="flex items-center mb-4">
            <Clock className="h-5 w-5 mr-2 text-indigo-600" />
            <h3 className="text-lg font-semibold text-gray-800">Analiza historyczna (dane z wizyt)</h3>
          </div>
          
          {data.historical_analysis.enabled ? (
            <div className="space-y-6">
              {/* Info Banner */}
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                <div className="flex items-start">
                  <Info className="h-5 w-5 text-indigo-600 mr-3 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-indigo-800">
                    <p className="font-medium mb-1">Dane z wizyt KAM</p>
                    <p>
                      Ta analiza używa danych zapisanych podczas wizyt KAM w salonach. 
                      Stan zatowarowania i ekspozycji jest brany z ostatniej wizyty przed końcem analizowanego okresu.
                    </p>
                    <div className="mt-2 flex flex-wrap gap-3 text-xs">
                      <span className="inline-flex items-center px-2 py-1 rounded bg-indigo-100">
                        <strong>Pokrycie:</strong>&nbsp;{data.historical_analysis.salons_with_visit_data} z {data.historical_analysis.total_salons_analyzed} salonów ({data.historical_analysis.coverage_pct}%)
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Stock Impact from Visits */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
                  <h4 className="text-sm font-semibold text-gray-700 mb-4 flex items-center">
                    <Package className="h-4 w-4 mr-2 text-gray-500" />
                    Wpływ zatowarowania
                  </h4>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between items-center p-3 bg-emerald-50 rounded-lg">
                      <div>
                        <span className="text-sm font-medium text-emerald-800">Z towarem</span>
                        <p className="text-xs text-emerald-600">{data.historical_analysis.stock_impact.with_stock.count} salonów</p>
                      </div>
                      <span className="text-lg font-bold text-emerald-700">
                        {data.historical_analysis.stock_impact.with_stock.avg_sales.toFixed(1)}
                      </span>
                    </div>
                    
                    <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                      <div>
                        <span className="text-sm font-medium text-gray-800">Bez towaru</span>
                        <p className="text-xs text-gray-600">{data.historical_analysis.stock_impact.without_stock.count} salonów</p>
                      </div>
                      <span className="text-lg font-bold text-gray-700">
                        {data.historical_analysis.stock_impact.without_stock.avg_sales.toFixed(1)}
                      </span>
                    </div>
                    
                    <div className={`flex items-center justify-center p-2 rounded-lg ${
                      data.historical_analysis.stock_impact.avg_sales_diff_pct > 0 
                        ? 'bg-green-100 text-green-800' 
                        : data.historical_analysis.stock_impact.avg_sales_diff_pct < 0 
                          ? 'bg-red-100 text-red-800' 
                          : 'bg-gray-100 text-gray-600'
                    }`}>
                      {data.historical_analysis.stock_impact.avg_sales_diff_pct > 0 ? (
                        <TrendingUp className="h-4 w-4 mr-1" />
                      ) : data.historical_analysis.stock_impact.avg_sales_diff_pct < 0 ? (
                        <TrendingDown className="h-4 w-4 mr-1" />
                      ) : null}
                      <span className="text-sm font-medium">
                        {data.historical_analysis.stock_impact.avg_sales_diff_pct > 0 ? '+' : ''}{data.historical_analysis.stock_impact.avg_sales_diff_pct}%
                      </span>
                    </div>
                  </div>
                </div>

                {/* Exposition Impact from Visits */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
                  <h4 className="text-sm font-semibold text-gray-700 mb-4 flex items-center">
                    <Palette className="h-4 w-4 mr-2 text-gray-500" />
                    Wpływ ekspozycji
                  </h4>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                      <div>
                        <span className="text-sm font-medium text-blue-800">Z ekspozycją</span>
                        <p className="text-xs text-blue-600">{data.historical_analysis.exposition_impact.with_exposition.count} salonów</p>
                      </div>
                      <span className="text-lg font-bold text-blue-700">
                        {data.historical_analysis.exposition_impact.with_exposition.avg_sales.toFixed(1)}
                      </span>
                    </div>
                    
                    <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                      <div>
                        <span className="text-sm font-medium text-gray-800">Bez ekspozycji</span>
                        <p className="text-xs text-gray-600">{data.historical_analysis.exposition_impact.without_exposition.count} salonów</p>
                      </div>
                      <span className="text-lg font-bold text-gray-700">
                        {data.historical_analysis.exposition_impact.without_exposition.avg_sales.toFixed(1)}
                      </span>
                    </div>
                    
                    <div className={`flex items-center justify-center p-2 rounded-lg ${
                      data.historical_analysis.exposition_impact.avg_sales_diff_pct > 0 
                        ? 'bg-green-100 text-green-800' 
                        : data.historical_analysis.exposition_impact.avg_sales_diff_pct < 0 
                          ? 'bg-red-100 text-red-800' 
                          : 'bg-gray-100 text-gray-600'
                    }`}>
                      {data.historical_analysis.exposition_impact.avg_sales_diff_pct > 0 ? (
                        <TrendingUp className="h-4 w-4 mr-1" />
                      ) : data.historical_analysis.exposition_impact.avg_sales_diff_pct < 0 ? (
                        <TrendingDown className="h-4 w-4 mr-1" />
                      ) : null}
                      <span className="text-sm font-medium">
                        {data.historical_analysis.exposition_impact.avg_sales_diff_pct > 0 ? '+' : ''}{data.historical_analysis.exposition_impact.avg_sales_diff_pct}%
                      </span>
                    </div>
                  </div>
                </div>

                {/* Ambassador Device Impact */}
                {data.historical_analysis.ambassador_impact && (
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
                    <h4 className="text-sm font-semibold text-gray-700 mb-4 flex items-center">
                      <Smartphone className="h-4 w-4 mr-2 text-gray-500" />
                      Urządzenie ambasadorskie
                    </h4>
                    
                    <div className="space-y-3">
                      <div className="flex justify-between items-center p-3 bg-purple-50 rounded-lg">
                        <div>
                          <span className="text-sm font-medium text-purple-800">Z urządzeniem</span>
                          <p className="text-xs text-purple-600">{data.historical_analysis.ambassador_impact.with_ambassador.count} salonów</p>
                        </div>
                        <span className="text-lg font-bold text-purple-700">
                          {data.historical_analysis.ambassador_impact.with_ambassador.avg_sales.toFixed(1)}
                        </span>
                      </div>
                      
                      <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                        <div>
                          <span className="text-sm font-medium text-gray-800">Bez urządzenia</span>
                          <p className="text-xs text-gray-600">{data.historical_analysis.ambassador_impact.without_ambassador.count} salonów</p>
                        </div>
                        <span className="text-lg font-bold text-gray-700">
                          {data.historical_analysis.ambassador_impact.without_ambassador.avg_sales.toFixed(1)}
                        </span>
                      </div>
                      
                      <div className={`flex items-center justify-center p-2 rounded-lg ${
                        data.historical_analysis.ambassador_impact.avg_sales_diff_pct > 0 
                          ? 'bg-green-100 text-green-800' 
                          : data.historical_analysis.ambassador_impact.avg_sales_diff_pct < 0 
                            ? 'bg-red-100 text-red-800' 
                            : 'bg-gray-100 text-gray-600'
                      }`}>
                        {data.historical_analysis.ambassador_impact.avg_sales_diff_pct > 0 ? (
                          <TrendingUp className="h-4 w-4 mr-1" />
                        ) : data.historical_analysis.ambassador_impact.avg_sales_diff_pct < 0 ? (
                          <TrendingDown className="h-4 w-4 mr-1" />
                        ) : null}
                        <span className="text-sm font-medium">
                          {data.historical_analysis.ambassador_impact.avg_sales_diff_pct > 0 ? '+' : ''}{data.historical_analysis.ambassador_impact.avg_sales_diff_pct}%
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Methodology Note */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-4">
                <div className="flex items-start">
                  <HelpCircle className="h-5 w-5 text-amber-600 mr-3 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-amber-800">
                    <p className="font-medium mb-1">Metodologia</p>
                    <p>
                      Dla każdego salonu bierzemy stan zatowarowania i ekspozycji z <strong>ostatniej wizyty</strong> przed końcem analizowanego okresu.
                      Analiza obejmuje tylko salony, które miały wizytę ({data.historical_analysis.coverage_pct}% pokrycia).
                      Jest to bardziej precyzyjne podejście niż analiza na podstawie obecnego stanu.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
              <Clock className="h-12 w-12 text-gray-400 mx-auto mb-3" />
              <p className="text-sm text-gray-600">
                {data.historical_analysis.reason || 'Brak danych z wizyt dla wybranego okresu.'}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default StockExposition
